import { yupResolver } from '@hookform/resolvers/yup';
import { useConferenceSignup } from 'api/conferences/conferenceSignup';
import { CONFERENCE_ID } from 'constants/constants';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { Button } from 'ui-library/components/Button/Button';
import { Checkbox } from 'ui-library/components/Checkbox/Checkbox';
import { TextInput } from 'ui-library/components/TextInput/TextInput';
import * as yup from 'yup';
import s from './ConferenceSignup.module.scss';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  conferenceCode: string;
  agreeToTerms: boolean;
}

const signupSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  conferenceCode: yup.string().required('Conference code is required'),
  agreeToTerms: yup.boolean().required().oneOf([true], 'You must agree to the Terms & Conditions'),
});

export const ConferenceSignup = () => {
  const [searchParams] = useSearchParams();
  const conferenceId = searchParams.get(CONFERENCE_ID);
  const [submitted, setSubmitted] = useState(false);

  const { mutateAsync: signup, isPending } = useConferenceSignup();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SignupFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      conferenceCode: '',
      agreeToTerms: false,
    },
    mode: 'onChange',
    resolver: yupResolver(signupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    if (!conferenceId) return;
    await signup(
      {
        conferenceId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        conferenceCode: data.conferenceCode,
        termsAndConditionsAccepted: data.agreeToTerms,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  if (!conferenceId) {
    return (
      <div className={s.page}>
        <div className={s.card}>
          <p className={s.errorState}>No conference specified. Please use a valid signup link.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={s.page}>
        <div className={`${s.card} ${s.successCard}`}>
          <h2 className={s.successTitle}>You're registered!</h2>
          <p className={s.successMessage}>Thank you for signing up. You will receive further details by email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.card}>
        <h1 className={s.title}>Conference Sign Up</h1>
        <p className={s.subtitle}>Fill in your details below to register for the conference.</p>

        <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="First Name"
                placeholder="First name"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Last Name"
                placeholder="Last name"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                type="email"
                label="Email"
                placeholder="you@example.com"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <Controller
            name="conferenceCode"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Conference Code"
                placeholder="Conference code"
                error={!!errors.conferenceCode}
                helperText={errors.conferenceCode?.message}
              />
            )}
          />

          <div className={s.checkboxGroup}>
            <a href="https://ankar.ai/terms-and-conditions" target="_blank" rel="noreferrer">
              Terms and Conditions
            </a>
            <Controller
              name="agreeToTerms"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={checked => field.onChange(checked)}
                  label="I agree to the Terms & Conditions"
                />
              )}
            />
            {errors.agreeToTerms && <p className={s.fieldError}>{errors.agreeToTerms.message}</p>}
          </div>

          <Button type="submit" disabled={isPending || !isValid} className={s.submitButton}>
            {isPending ? 'Signing up...' : 'Sign Up'}
          </Button>
        </form>
      </div>
    </div>
  );
};
